import { useCallback, useEffect, useRef } from 'react';

import {
  SLABCLOUD_ORIGIN,
  UseVisualizerMessageParams,
  UseVisualizerMessageReturn,
  VisualizerMessageData,
} from '../ColorVizContent.types';
import {
  getCountertopColorName,
  getVisualizerIframe,
  hasColorChanged,
  isValidVisualizerMessage,
} from '../ColorVizContent.utils';

/**
 * Custom hook for handling visualizer iframe message communication.
 *
 * Features:
 * - Proper event listener cleanup on unmount (prevents memory leaks)
 * - Origin validation for security (only accepts messages from slabcloud.com)
 * - Request tracking to ignore stale/unrelated messages
 * - Memoized handlers with proper dependencies
 */
export const useVisualizerMessage = ({
  selectedColorName,
  colorVizName,
  onColorChange,
  onClose,
}: UseVisualizerMessageParams): UseVisualizerMessageReturn => {
  // Track if we're expecting a response to avoid processing unrelated messages
  const isWaitingForResponseRef = useRef(false);

  const handleMessage = useCallback(
    (event: MessageEvent<VisualizerMessageData>) => {
      // SECURITY: Validate message origin to prevent cross-site scripting attacks
      if (event.origin !== SLABCLOUD_ORIGIN) {
        return;
      }

      // Only process messages when we've requested a screenshot
      if (!isWaitingForResponseRef.current) {
        return;
      }

      const messageData = event.data;

      // Validate message contains required data
      if (!isValidVisualizerMessage(messageData)) {
        return;
      }

      // Guard: Need selected color name to compare
      if (!selectedColorName) {
        return;
      }

      // Reset waiting flag - we've received our response
      isWaitingForResponseRef.current = false;

      // Extract countertop color from applied materials
      const countertopColorName = getCountertopColorName(
        messageData.appliedMaterials
      );

      // If user changed color in visualizer, trigger callback to show confirmation
      if (hasColorChanged(countertopColorName, selectedColorName)) {
        onColorChange(
          messageData.screenshotImg,
          countertopColorName ?? colorVizName
        );
      }
    },
    [selectedColorName, colorVizName, onColorChange]
  );

  // Set up message event listener with proper cleanup
  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      isWaitingForResponseRef.current = false;
    };
  }, [handleMessage]);

  const requestScreenshot = useCallback(() => {
    const iframe = getVisualizerIframe();

    if (!iframe?.contentWindow) {
      // Iframe not available, just close the modal
      onClose();

      return;
    }

    // Mark that we're waiting for a response
    isWaitingForResponseRef.current = true;

    // Request screenshot from visualizer
    iframe.contentWindow.postMessage(
      { action: 'getScreenshot' },
      SLABCLOUD_ORIGIN
    );

    // Close the visualizer modal immediately (original behavior)
    // The message handler will show confirmation modal if color changed
    onClose();
  }, [onClose]);

  return { requestScreenshot };
};
